/**
 * Drop-off ledger: the pure rules behind the admin sidebar's Drop-off block.
 * Plan: docs/roadmap/DROPOFF_LEDGER_PLAN.md (§4 statuses, §5 Q2 days in
 * care; the §3 paper-vs-electronic MCCD split was dropped by T-051). The partner portal (aura-app) applies the
 * same rules; keep the two in step.
 */
import { cremationScheduled } from '../components/dispatch/partnerDocRequirements';

/** The facility's time zone. "Day N" counts calendar days here. */
export const DROPOFF_TZ = 'America/Toronto';

export interface DropoffLedgerFacts {
  cremation_status?: string | null;
  cremation_date?: string | Date | null;
  dropoff_partner_id?: string | number | null;
  dropoff_released_at?: string | null;
  dropoff_received_at?: string | null;
  dropoff_implant?: 'no' | 'yes' | 'unknown' | null;
  pacemaker_removed_at?: string | null;
  dropoff_belongings?: string | null;
  dropoff_belongings_mailed_at?: string | null;
}

export type DropoffStatus = 'on_the_way' | 'in_care' | 'released' | 'completed';

export const DROPOFF_STATUS_LABEL: Record<DropoffStatus, string> = {
  on_the_way: 'On the way',
  in_care: 'In care',
  released: 'Released',
  completed: 'Completed',
};

const COMPLETED = ['ready_for_delivery', 'delivery_scheduled', 'delivered'];

/**
 * Handed off = the facility clicked "Released to crematorium", or Cleo
 * scheduled the cremation (§4, the dropoffHandoff.ts rule from PR #83).
 */
export function dropoffHandedOff(f: DropoffLedgerFacts): boolean {
  return !!f.dropoff_released_at || cremationScheduled(f.cremation_status);
}

/** Which trigger handed the body off, for the Released subline. */
export function dropoffReleaseTrigger(
  f: DropoffLedgerFacts
): 'facility' | 'cleo' | null {
  if (f.dropoff_released_at) return 'facility';
  if (cremationScheduled(f.cremation_status)) return 'cleo';
  return null;
}

export function dropoffStatus(f: DropoffLedgerFacts): DropoffStatus {
  if (COMPLETED.includes(f.cremation_status || '')) return 'completed';
  if (dropoffHandedOff(f)) return 'released';
  if (f.dropoff_received_at) return 'in_care';
  return 'on_the_way';
}

/** YYYY-MM-DD of an instant, in the facility's time zone. */
export function torontoDay(at: string | Date): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DROPOFF_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(at));
}

/**
 * When the stay ends, for counting: the facility's release; else, once Cleo
 * handed off by scheduling the cremation, the cremation date; else (still in
 * care) now. null = handed off with no date to count to.
 */
export function dropoffStayEnd(
  f: DropoffLedgerFacts,
  now: Date = new Date()
): Date | null {
  if (f.dropoff_released_at) return new Date(f.dropoff_released_at);
  if (dropoffHandedOff(f)) return f.cremation_date ? new Date(f.cremation_date) : null;
  return now;
}

export interface DaysInCare {
  /** Calendar days, arrival day = Day 0 (§5 Q2). */
  day: number;
  /** Approximate hours on site, arrival → release (or now). */
  hours: number;
  /** false while still in care (counting to now). */
  ended: boolean;
}

/** Day N + approximate hours. null when there's no arrival (or no end) to count from. */
export function daysInCare(
  f: DropoffLedgerFacts,
  now: Date = new Date()
): DaysInCare | null {
  if (!f.dropoff_received_at) return null;
  const end = dropoffStayEnd(f, now);
  if (!end) return null;
  const start = new Date(f.dropoff_received_at);
  const dayMs = 86_400_000;
  const day = Math.round(
    (Date.parse(torontoDay(end)) - Date.parse(torontoDay(start))) / dayMs
  );
  const hours = Math.max(0, Math.round((end.getTime() - start.getTime()) / 3_600_000));
  return { day: Math.max(0, day), hours, ended: end !== now };
}

export type MccdStatus = 'on_file' | 'missing';

export const MCCD_STATUS_LABEL: Record<MccdStatus, string> = {
  on_file: 'On file',
  missing: 'Missing',
};

/**
 * T-051: only whether an active medical_certificate is on file for the case
 * matters, not who sent it (the facility's scan of the paper that came with
 * the body, or the electronic copy Cleo received).
 */
export function mccdStatus(onFile: boolean): MccdStatus {
  return onFile ? 'on_file' : 'missing';
}

/**
 * The one MCCD nudge: the body has arrived and no certificate is on file yet.
 * Before arrival a missing certificate is expected, not a problem.
 */
export function mccdMissingAfterArrival(
  f: DropoffLedgerFacts,
  onFile: boolean
): boolean {
  return !!f.dropoff_received_at && !onFile;
}

/** Short date in the facility's time zone: "Sep 22". */
export function shortDay(at: string | Date): string {
  return new Date(at).toLocaleDateString('en-US', {
    timeZone: DROPOFF_TZ,
    month: 'short',
    day: 'numeric',
  });
}

/** "Sep 21, 9:15 AM" in the facility's time zone. */
export function shortDateTime(at: string | Date): string {
  return new Date(at).toLocaleString('en-US', {
    timeZone: DROPOFF_TZ,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Pacemaker line: the intake answer, plus removal when there was one. */
export function pacemakerLabel(f: DropoffLedgerFacts): string {
  if (f.pacemaker_removed_at) return `Removed ${shortDay(f.pacemaker_removed_at)}`;
  switch (f.dropoff_implant) {
    case 'yes':
      return 'Yes · not removed';
    case 'no':
      return 'None';
    case 'unknown':
      return 'Unknown';
    default:
      return 'Not recorded';
  }
}

/** Same cap as the partner portal's belongings field (aura-app MAX_BELONGINGS). */
export const MAX_BELONGINGS = 500;

/**
 * What staff typed, stored the way the portal stores it: trimmed, capped at
 * MAX_BELONGINGS, and blank = null (shown as "None recorded").
 */
export function normalizeBelongings(input: string | null | undefined): string | null {
  const trimmed = (input ?? '').trim();
  return trimmed ? trimmed.slice(0, MAX_BELONGINGS) : null;
}

/** Belongings line: what came in, and whether it was mailed back. */
export function belongingsLabel(f: DropoffLedgerFacts): string {
  const what = (f.dropoff_belongings || '').trim();
  if (!what) return f.dropoff_belongings_mailed_at ? 'Mailed back' : 'None recorded';
  return `${what} · ${f.dropoff_belongings_mailed_at ? 'mailed back' : 'not mailed back'}`;
}
